import { forwardRef, ReactNode } from "react";

export type Props = {
  /** Image source URL */
  src?: string;
  /** Alt text for the image */
  alt?: string;
  /** Size in pixels (used for width and height) */
  size?: number;
  /** Additional CSS classes */
  className?: string;
  /** Additional CSS classes for the image */
  imageClassName?: string;
  /** Custom fallback renderer when no image is provided */
  renderFallback?: (props: { alt?: string; size?: number }) => ReactNode;
  /** Called once the image has loaded, for callers that reveal it only then */
  onImageLoad?: () => void;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Base avatar component with no styling.
 * Use this as the foundation for styled avatar components.
 *
 * @example
 * // In shared/ui/Avatar.tsx
 * import { AvatarBase, type Props as AvatarBaseProps } from "@core/ui";
 *
 * export function Avatar({ className, ...props }: AvatarBaseProps) {
 *   return (
 *     <AvatarBase
 *       className={twMerge("rounded-full overflow-hidden", className)}
 *       renderFallback={({ alt }) => (
 *         <div className="w-full h-full bg-gray-200 flex items-center justify-center">
 *           {alt?.charAt(0).toUpperCase()}
 *         </div>
 *       )}
 *       {...props}
 *     />
 *   );
 * }
 */
export const AvatarBase = forwardRef<HTMLDivElement, Props>(
  (
    {
      src,
      alt,
      size = 40,
      className,
      imageClassName,
      renderFallback,
      onImageLoad,
      "data-testid": testId = "avatar",
    },
    ref
  ) => {
    // Default fallback shows first letter of alt text
    const defaultFallback = (
      <span data-avatar-fallback>{alt ? alt.charAt(0).toUpperCase() : "?"}</span>
    );

    return (
      <div
        ref={ref}
        className={className}
        style={{ width: size, height: size }}
        data-testid={testId}
      >
        {src ? (
          <img
            src={src}
            alt={alt || "Avatar"}
            className={imageClassName}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onLoad={onImageLoad}
            data-avatar-image
          />
        ) : renderFallback ? (
          renderFallback({ alt, size })
        ) : (
          defaultFallback
        )}
      </div>
    );
  }
);

AvatarBase.displayName = "AvatarBase";


